import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import React, { useState, useEffect } from 'react';

import { FieldLabel } from './MobileStatusUpdateDialog.styles';
import {
  ParameterDataFormParameterType,
  ParameterData,
} from '../../../../../../@types/form-builder.type';
import { LocationNodeData } from '../../../../../../@types';
import { useSearchAllocationNodes } from '../../../../../../queries/live-search-query';

interface MobileParameterFieldProps {
  preSelectedData: ParameterData;
  value: any;
  onChange: (value: any) => void;
  isRequired?: boolean;
}

export const MobileParameterField: React.FC<MobileParameterFieldProps> = ({
  preSelectedData,
  value,
  onChange,
  isRequired,
}) => {
  const parameterType = preSelectedData?.parameterType;

  // Allocation search
  if (
    parameterType === ParameterDataFormParameterType.PrimaryAllocation ||
    parameterType === ParameterDataFormParameterType.SecondaryAllocation
  ) {
    return (
      <AllocationSearchField
        label={parameterType}
        value={value}
        onChange={onChange}
        isRequired={isRequired}
        preSelectedData={preSelectedData}
      />
    );
  }

  // Scheduled Date
  if (parameterType === ParameterDataFormParameterType.ScheduledDate) {
    return (
      <TextField
        fullWidth
        size="small"
        type="datetime-local"
        label="Scheduled Date/Time"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        required={isRequired}
        InputLabelProps={{ shrink: true }}
      />
    );
  }

  // Default: show parameter type as info
  return (
    <FieldLabel style={{ fontStyle: 'italic', opacity: 0.7 }}>
      {parameterType}: Will be set automatically
    </FieldLabel>
  );
};

interface AllocationSearchFieldProps {
  label: string;
  value: any;
  onChange: (value: any) => void;
  isRequired?: boolean;
  preSelectedData: ParameterData;
}

const AllocationSearchField: React.FC<AllocationSearchFieldProps> = ({
  label,
  value,
  onChange,
  isRequired,
  preSelectedData,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<LocationNodeData[]>([]);
  const [loading, setLoading] = useState(false);

  const searchAllocationNodes = useSearchAllocationNodes();

  // Sync inputValue with value prop when parent pre-populates the field
  // Note: inputValue is intentionally NOT in deps - adding it would cause the effect
  // to override user typing when they search. We only want to sync when the parent
  // value changes, not when the user is actively typing in the search field.
  useEffect(() => {
    if (value?.name && inputValue !== value.name) {
      setInputValue(value.name);
    }
  }, [value?.name]);

  // Pre-populate with presetSuggestion if available and no value set yet
  // Note: Intentionally limited deps - only trigger on presetSuggestion changes,
  // not on onChange/value changes which would cause infinite loops
  useEffect(() => {
    if (preSelectedData?.presetSuggestion1?.name && !value) {
      searchAllocationNodes
        .mutateAsync(preSelectedData.presetSuggestion1.name)
        .then((results) => {
          const match = results.find((r) => r.code === preSelectedData.presetSuggestion1?.code);
          if (match) {
            onChange(match);
            setInputValue(match.name);
          }
        })
        .catch(() => {
          // Silently handle search errors during prefill
        });
    }
  }, [preSelectedData?.presetSuggestion1?.name, preSelectedData?.presetSuggestion1?.code]);

  const handleSearch = async (searchText: string): Promise<void> => {
    if (!searchText || searchText.length < 2) {
      setOptions([]);
      return;
    }

    setLoading(true);
    try {
      const results = await searchAllocationNodes.mutateAsync(searchText);
      setOptions(results || []);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Autocomplete
      fullWidth
      size="small"
      options={options}
      loading={loading}
      value={value || null}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);
        handleSearch(newInputValue);
      }}
      onChange={(_, newValue) => {
        onChange(newValue);
      }}
      getOptionLabel={(option) => option?.name || ''}
      isOptionEqualToValue={(option, val) => option?.code === val?.code}
      renderOption={(props, option) => (
        <li {...props} key={option.code}>
          <div>
            <div style={{ fontWeight: 500 }}>{option.name}</div>
            {option.nodeTypeName && (
              <div style={{ fontSize: '12px', opacity: 0.7 }}>{option.nodeTypeName}</div>
            )}
          </div>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={isRequired}
          placeholder="Search..."
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={18} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
